import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, ExternalLink, Edit, Lock, Unlock, RefreshCw,
  ChevronDown, ChevronRight, Globe, Briefcase, MapPin, Euro, Layers,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  adminApi,
  type AdminPostingDetail,
  type AdminPostingSibling,
  type AdminPositionContext,
} from '@/services/adminApi';

const CHANNEL_LABELS: Record<string, string> = {
  company_website: 'Sito aziendale',
  linkedin: 'LinkedIn',
  referral: 'Referral',
  other: 'Altro',
};

function channelLabel(channel: string | null | undefined): string {
  if (!channel) return '—';
  return CHANNEL_LABELS[channel] ?? channel;
}

/**
 * AdminPostingDetailPage — single posting deep-dive.
 *
 * Read-only by design (Backoffice MVP / BO-01). Future write actions
 * (close posting, force-rebuild URL, ...) will land in BO-02.
 *
 * The page shows resolved company + position labels at the top so the
 * admin always knows "whose posting is this", an "Open public URL"
 * external link if the posting has a `url` field, and a raw JSON dump
 * for fields not yet first-class — convenient for ops debugging without
 * needing the Firestore console.
 */
export default function AdminPostingDetailPage() {
  const { companyId = '', posId = '', postingId = '' } =
    useParams<{ companyId: string; posId: string; postingId: string }>();
  const [data, setData] = useState<AdminPostingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Action dialogs
  const [editOpen, setEditOpen] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);
  const [pendingReopen, setPendingReopen] = useState(false);
  const [pendingRegenerate, setPendingRegenerate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail = await adminApi.postings.detail(companyId, posId, postingId);
        if (!cancelled) setData(detail);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, posId, postingId, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to="/admin/postings"><ArrowLeft className="h-4 w-4" /> Back to postings</Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold">{data.title || '(untitled posting)'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <Link to={`/admin/customers/${data.companyId}`} className="hover:underline">
              {data.companyName || data.companyId}
            </Link>
            {' · '}
            <Link
              to={`/admin/customers/${data.companyId}/positions/${data.jobPositionId}`}
              className="hover:underline"
            >
              {data.positionTitle || data.jobPositionId}
            </Link>
            {' · '}
            <span>{data.channel || 'no channel'}</span>
            {' · '}
            {data.isActive ? (
              <span className="text-success">active</span>
            ) : (
              <span>inactive</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground font-mono mt-1">{data.postingId}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditOpen(true)}>
            <Edit className="h-3.5 w-3.5" /> Edit
          </Button>
          {data.isActive ? (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPendingClose(true)}>
              <Lock className="h-3.5 w-3.5" /> Close
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPendingReopen(true)}>
              <Unlock className="h-3.5 w-3.5" /> Reopen
            </Button>
          )}
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPendingRegenerate(true)}>
            <RefreshCw className="h-3.5 w-3.5" /> Regenerate URL
          </Button>
        </div>
      </div>

      {/* Position content card (description markdown + metadati) */}
      {data.position && <PositionContentCard position={data.position} />}

      {/* Publications card — tutti i posting (canali) della stessa position */}
      {data.siblings && data.siblings.length > 0 && (
        <PublicationsCard
          siblings={data.siblings}
          companyId={data.companyId}
          jobPositionId={data.jobPositionId}
        />
      )}

      {/* Quick facts (posting tecnico + timestamps) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Posting tecnico
            </h2>
            <Row k="Posting ID" v={<code className="text-[10px] font-mono">{data.postingId}</code>} />
            <Row k="Canale" v={channelLabel(data.channel)} />
            <Row k="Visibilità" v={data.visibility || '—'} />
            <Row k="Slug" v={data.slug || '—'} />
            {data.url && (
              <div className="pt-2">
                <Button asChild variant="outline" size="sm" className="gap-1.5">
                  <a href={data.url} target="_blank" rel="noopener noreferrer">
                    Apri URL pubblica <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Timestamps
            </h2>
            <Row k="Creato" v={fmtDate(data.createdAt)} />
            <Row k="Pubblicato" v={fmtDate(data.publishedAt)} />
            <Row k="Aggiornato" v={fmtDate(data.updatedAt)} />
            <Row k="Chiuso" v={fmtDate(data.closedAt)} />
          </CardContent>
        </Card>
      </div>

      {/* Drill-down */}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/applications?companyId=${data.companyId}`}>
            Vedi candidature di questa azienda
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/customers/${data.companyId}`}>Apri azienda</Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/customers/${data.companyId}/positions/${data.jobPositionId}`}>
            Apri posizione parent
          </Link>
        </Button>
      </div>

      {/* Raw JSON collapsed (debug only) */}
      <RawJsonCollapsible raw={data.raw} />

      {/* Dialogs */}
      <EditPostingDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        posting={data}
        onSaved={() => { setEditOpen(false); refresh(); }}
      />
      <ReasonConfirmDialog
        open={pendingClose}
        onOpenChange={setPendingClose}
        title="Close posting?"
        description="The posting will stop accepting new applications. The public URL returns 404."
        confirmLabel="Close"
        destructive
        onConfirm={async (reason) => {
          await adminApi.postings.close(companyId, posId, postingId, reason);
          toast.success('Posting closed');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingReopen}
        onOpenChange={setPendingReopen}
        title="Reopen posting?"
        description="The posting starts accepting applications again at the same URL."
        confirmLabel="Reopen"
        onConfirm={async (reason) => {
          await adminApi.postings.reopen(companyId, posId, postingId, reason);
          toast.success('Posting reopened');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingRegenerate}
        onOpenChange={setPendingRegenerate}
        title="Regenerate landing URL?"
        description="Rebuilds landingPageUrl against the current company slug + storefront base URL. Existing links shared with candidates may stop working if the slug also changed."
        confirmLabel="Regenerate"
        destructive
        onConfirm={async (reason) => {
          const r = await adminApi.postings.regenerateUrl(companyId, posId, postingId, reason);
          toast.success('Landing URL regenerated', { description: r.landingPageUrl });
          refresh();
        }}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Section components (sprint admin_posting_detail_richness 30/05/26)
// ----------------------------------------------------------------------------

function PositionContentCard({ position }: { position: AdminPositionContext }) {
  const salary = position.salaryMin || position.salaryMax
    ? `${position.salaryMin ?? '?'} - ${position.salaryMax ?? '?'} ${position.salaryCurrency ?? 'EUR'}`
    : null;

  return (
    <Card className="border-border/50">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-2">
          <Briefcase className="h-4 w-4 text-primary shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Contenuto posizione
            </h2>
            <h3 className="text-lg font-semibold mt-1">{position.title || '(senza titolo)'}</h3>
          </div>
          {position.status && (
            <Badge variant={position.status === 'active' ? 'default' : 'secondary'} className="capitalize">
              {position.status}
            </Badge>
          )}
        </div>

        {/* Metadati */}
        <div className="flex flex-wrap gap-2 text-xs">
          {position.seniority && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted">
              <Layers className="h-3 w-3" /> {position.seniority}
            </span>
          )}
          {position.contractType && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted">
              {position.contractType}
            </span>
          )}
          {position.workMode && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted">
              {position.workMode}
            </span>
          )}
          {position.location && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted">
              <MapPin className="h-3 w-3" /> {position.location}
            </span>
          )}
          {salary && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted">
              <Euro className="h-3 w-3" /> {salary}
            </span>
          )}
          {position.outputLanguage && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted uppercase">
              {position.outputLanguage}
            </span>
          )}
        </div>

        {/* Descrizione markdown rendered */}
        {position.description ? (
          <div className="prose prose-sm max-w-none dark:prose-invert mt-3 border-t border-border/50 pt-4">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{position.description}</ReactMarkdown>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic mt-3 border-t border-border/50 pt-4">
            Nessuna descrizione popolata per questa posizione.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PublicationsCard({
  siblings,
  companyId,
  jobPositionId,
}: {
  siblings: AdminPostingSibling[];
  companyId: string;
  jobPositionId: string;
}) {
  const activeCount = siblings.filter((s) => s.isActive).length;
  return (
    <Card className="border-border/50">
      <CardContent className="p-0">
        <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold">Canali di pubblicazione</h2>
            <p className="text-xs text-muted-foreground">
              {siblings.length} totali · {activeCount} attivi
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Canale</th>
                <th className="text-left px-3 py-2">Stato</th>
                <th className="text-left px-3 py-2">Visibilità</th>
                <th className="text-left px-3 py-2">Creato</th>
                <th className="text-left px-3 py-2">Pubblicato</th>
                <th className="text-left px-3 py-2">URL</th>
                <th className="text-right px-3 py-2">Azione</th>
              </tr>
            </thead>
            <tbody>
              {siblings.map((s) => {
                const detailHref = `/admin/customers/${companyId}/positions/${jobPositionId}/postings/${s.postingId}`;
                return (
                  <tr
                    key={s.postingId}
                    className={cn(
                      'border-t border-border/30',
                      s.isCurrent && 'bg-primary/5',
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{channelLabel(s.channel)}</span>
                        {s.isCurrent && (
                          <Badge variant="default" className="text-[9px] py-0 px-1.5">CORRENTE</Badge>
                        )}
                      </div>
                      <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                        {s.postingId.slice(0, 12)}…
                      </p>
                    </td>
                    <td className="px-3 py-3">
                      {s.isActive ? (
                        <span className="text-xs text-success font-medium">attivo</span>
                      ) : s.closedAt ? (
                        <span className="text-xs text-muted-foreground">chiuso</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">inattivo</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground capitalize">
                      {s.visibility ?? '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">
                      {s.createdAt ? new Date(s.createdAt).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">
                      {s.publishedAt ? new Date(s.publishedAt).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="px-3 py-3">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                        >
                          Apri <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {!s.isCurrent && (
                        <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                          <Link to={detailHref}>Apri</Link>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function RawJsonCollapsible({ raw }: { raw: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="border-border/50">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full px-5 py-3 border-b border-border/50 flex items-center gap-2 hover:bg-accent/30 transition-colors text-left"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <h2 className="text-sm font-semibold">Raw posting document (debug)</h2>
        </button>
        {open && (
          <pre className="p-5 text-xs overflow-x-auto bg-muted/30 max-h-[400px]">
            {JSON.stringify(raw, null, 2)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

function EditPostingDialog({
  open, onOpenChange, posting, onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  posting: AdminPostingDetail;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(posting.title ?? '');
  const [channel, setChannel] = useState(posting.channel ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(posting.title ?? '');
      setChannel(posting.channel ?? '');
      setReason('');
    }
  }, [open, posting]);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      const payload: Parameters<typeof adminApi.postings.update>[3] = { reason };
      if (title !== posting.title) payload.title = title;
      if (channel !== posting.channel) payload.channel = channel;
      const keys = Object.keys(payload).filter((k) => k !== 'reason');
      if (keys.length === 0) {
        toast.info('No changes to save.');
        return;
      }
      await adminApi.postings.update(posting.companyId, posting.jobPositionId, posting.postingId, payload);
      toast.success('Posting updated');
      onSaved();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit posting</DialogTitle>
          <DialogDescription>Updating posting {posting.postingId}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="post-title">Title</Label>
            <Input id="post-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-channel">Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger id="post-channel"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company_website">Website</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="post-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()}>{busy ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonConfirmDialog({
  open, onOpenChange, title, description, confirmLabel, destructive, onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="posting-reason-confirm">Reason <span className="text-destructive">*</span></Label>
          <Textarea id="posting-reason-confirm" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={busy || !reason.trim()}
            className={destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {busy ? 'Working...' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-muted-foreground w-28 shrink-0">{k}</span>
      <span className="font-medium break-all">{v}</span>
    </div>
  );
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB');
}
